import { useCallback, useMemo } from "react";
import type * as Y from "yjs";
import {
  type Cable,
  type CableId,
  cableId as mkCableId,
  type Conductor,
  conductorId,
  createDeviceFromSymbol,
  deleteLayoutsForSheet,
  deleteRoutingsForSheet,
  deleteSheet as domainDeleteSheet,
  type DeviceId,
  type DeviceOnSheet,
  type DomainMaps,
  getDeviceLayout,
  listCables,
  listConductors,
  listDeviceLayoutsForSheet,
  listDevices,
  listSheetsForDevice,
  listSheetsInOrder,
  listTerminals,
  moveSheetInOrder,
  newId,
  putCable,
  putConductor,
  putDevice,
  putDeviceLayout,
  putSheet,
  putTerminal,
  removeConductorFromAnyCable,
  runLocal,
  type Sheet,
  sheetId as mkSheetId,
  type SheetId,
  type Terminal,
  type TerminalId,
} from "@electro-planner/domain";
import { getSymbol, type SymbolDefinition } from "@electro-planner/symbols";

export type ClipboardPayload = {
  devices: Array<{
    oldId: string;
    symbolId: string;
    position: { x: number; y: number };
    rotation: 0 | 90 | 180 | 270;
  }>;
  conductors: Array<{
    fromDeviceOldId: string;
    fromSymbolTerminalId: string;
    toDeviceOldId: string;
    toSymbolTerminalId: string;
    attributes: Record<string, unknown>;
  }>;
};

export type DomainMutations = {
  addDeviceFromSymbol: (
    symbolOrId: string | SymbolDefinition,
    position: { x: number; y: number },
  ) => DeviceId | null;
  moveDevice: (deviceId: DeviceId, position: { x: number; y: number }) => void;
  nudgeDevices: (deviceIds: DeviceId[], dx: number, dy: number) => void;
  rotateDevice: (deviceId: DeviceId, delta?: number) => void;
  setDeviceRotation: (deviceId: DeviceId, rotation: 0 | 90 | 180 | 270) => void;
  renameDevice: (deviceId: DeviceId, designation: string) => void;
  connect: (args: {
    sourceDeviceId: string;
    sourceHandleId: string;
    targetDeviceId: string;
    targetHandleId: string;
  }) => void;
  deleteDevice: (deviceId: DeviceId) => void;
  deleteConductor: (conductorId: string) => void;
  /**
   * Patchar en conductors attributes (area, color, circuitLabel). Undefined/tom
   * sträng tolkas som "ta bort attributet" — så att användaren kan avmarkera
   * ett värde i EdgeInspector och få default-stroke tillbaka.
   */
  setConductorAttributes: (
    conductorId: string,
    patch: {
      area?: number | null;
      color?: string | null;
      circuitLabel?: string | null;
    },
  ) => void;
  buildClipboardPayload: (deviceIds: string[]) => ClipboardPayload;
  pasteClipboard: (
    payload: ClipboardPayload,
    offset: { x: number; y: number },
  ) => DeviceId[];
  addSheet: (opts?: { title?: string }) => SheetId;
  /** Klonar ett helt ark (devices + conductors + cables) till ett nytt. */
  duplicateSheet: (sourceSheetId: SheetId) => SheetId | null;
  renameSheet: (sheetId: SheetId, title: string) => void;
  /**
   * Tar bort sheet + alla layouts/routings på det, och cascade-raderar devices
   * som därmed blir föräldralösa. Returnerar false om det är det enda arket
   * (vi tillåter inte en ritning utan ark).
   */
  deleteSheet: (sheetId: SheetId) => boolean;
  moveSheet: (sheetId: SheetId, newIndex: number) => void;
  /**
   * Grupperar conductors till en ny Cable med auto-nummer (W1, W2…). Conductors
   * som redan ligger i en annan kabel flyttas över. Returnerar nya CableId, eller
   * null om listan är tom.
   */
  groupConductorsAsCable: (
    conductorIds: string[],
    opts?: { designation?: string; type?: string },
  ) => CableId | null;
  /** Plockar ut conductorn ur sin kabel. Tom kabel raderas automatiskt. */
  leaveCable: (conductorId: string) => void;
  renameCable: (cableId: CableId, designation: string) => void;
  setCableType: (cableId: CableId, type: string) => void;
};

export function useDomainMutations(
  doc: Y.Doc,
  maps: DomainMaps,
  sheetId: SheetId,
): DomainMutations {
  const addDeviceFromSymbol = useCallback<DomainMutations["addDeviceFromSymbol"]>(
    (symbolOrId, position) => {
      const symbol =
        typeof symbolOrId === "string" ? getSymbol(symbolOrId) : symbolOrId;
      if (!symbol) return null;

      return runLocal(doc, () => {
        const existing = listDevices(maps);
        const { device, terminals } = createDeviceFromSymbol(symbol, {
          existingDevices: existing,
          symbolId: symbol.id,
        });
        putDevice(maps, device);
        for (const t of terminals) putTerminal(maps, t);
        const layout: DeviceOnSheet = {
          deviceId: device.id,
          sheetId,
          x: position.x,
          y: position.y,
          rotation: 0,
        };
        putDeviceLayout(maps, layout);
        return device.id;
      });
    },
    [doc, maps, sheetId],
  );

  const moveDevice = useCallback<DomainMutations["moveDevice"]>(
    (deviceId, position) => {
      runLocal(doc, () => {
        // Bevara existerande rotation — annars nollställer varje drag den.
        const prev = getDeviceLayout(maps, deviceId, sheetId);
        const layout: DeviceOnSheet = {
          deviceId,
          sheetId,
          x: position.x,
          y: position.y,
          rotation: prev?.rotation ?? 0,
        };
        putDeviceLayout(maps, layout);
      });
    },
    [doc, maps, sheetId],
  );

  // Förskjuter flera enheter i samma transaktion — annars får vi ett undo-steg
  // per pil-tryck * antal markerade symboler, vilket blir oanvändbart.
  const nudgeDevices = useCallback<DomainMutations["nudgeDevices"]>(
    (deviceIds, dx, dy) => {
      if (deviceIds.length === 0) return;
      if (dx === 0 && dy === 0) return;
      runLocal(doc, () => {
        for (const deviceId of deviceIds) {
          const prev = getDeviceLayout(maps, deviceId, sheetId);
          if (!prev) continue;
          putDeviceLayout(maps, {
            deviceId,
            sheetId,
            x: prev.x + dx,
            y: prev.y + dy,
            rotation: prev.rotation,
          });
        }
      });
    },
    [doc, maps, sheetId],
  );

  const rotateDevice = useCallback<DomainMutations["rotateDevice"]>(
    (deviceId, delta = 90) => {
      runLocal(doc, () => {
        const prev = getDeviceLayout(maps, deviceId, sheetId);
        if (!prev) return;
        const next = (((prev.rotation + delta) % 360) + 360) % 360;
        // Snap till planens tillåtna värden 0/90/180/270
        const snapped = (Math.round(next / 90) * 90) % 360;
        putDeviceLayout(maps, {
          ...prev,
          rotation: snapped as 0 | 90 | 180 | 270,
        });
      });
    },
    [doc, maps, sheetId],
  );

  const setDeviceRotation = useCallback<DomainMutations["setDeviceRotation"]>(
    (deviceId, rotation) => {
      runLocal(doc, () => {
        const prev = getDeviceLayout(maps, deviceId, sheetId);
        if (!prev) return;
        putDeviceLayout(maps, { ...prev, rotation });
      });
    },
    [doc, maps, sheetId],
  );

  const renameDevice = useCallback<DomainMutations["renameDevice"]>(
    (deviceId, designation) => {
      runLocal(doc, () => {
        const m = maps.devices.get(deviceId);
        if (!m) return;
        m.set("designation", designation);
      });
    },
    [doc, maps],
  );

  const connect = useCallback<DomainMutations["connect"]>(
    ({ sourceDeviceId, sourceHandleId, targetDeviceId, targetHandleId }) => {
      runLocal(doc, () => {
        const terminals = listTerminals(maps);
        const fromT = findTerminal(terminals, sourceDeviceId, sourceHandleId);
        const toT = findTerminal(terminals, targetDeviceId, targetHandleId);
        if (!fromT || !toT) return;
        const cond: Conductor = {
          id: conductorId(newId("cond")),
          fromTerminal: fromT.id,
          toTerminal: toT.id,
          attributes: {},
        };
        putConductor(maps, cond);
      });
    },
    [doc, maps],
  );

  const deleteDevice = useCallback<DomainMutations["deleteDevice"]>(
    (id) => {
      runLocal(doc, () => {
        cascadeDeleteDevice(maps, id);
      });
    },
    [doc, maps],
  );

  const deleteConductor = useCallback<DomainMutations["deleteConductor"]>(
    (id) => {
      runLocal(doc, () => {
        // Håll cables konsistenta: en raderad conductor får inte ligga kvar som
        // dangling ref i en kabel. Tom kabel raderas i samma transaktion.
        removeConductorFromAnyCable(maps, conductorId(id));
        maps.conductors.delete(id);
      });
    },
    [doc, maps],
  );

  const setConductorAttributes = useCallback<
    DomainMutations["setConductorAttributes"]
  >(
    (id, patch) => {
      runLocal(doc, () => {
        const m = maps.conductors.get(id);
        if (!m) return;
        const prev = (m.get("attributes") as Conductor["attributes"]) ?? {};
        const next: Conductor["attributes"] = { ...prev };
        if ("area" in patch) {
          if (patch.area == null || !Number.isFinite(patch.area)) delete next.area;
          else next.area = patch.area;
        }
        if ("color" in patch) {
          const trimmed = typeof patch.color === "string" ? patch.color.trim() : "";
          if (!trimmed) delete next.color;
          else next.color = trimmed;
        }
        if ("circuitLabel" in patch) {
          const trimmed =
            typeof patch.circuitLabel === "string" ? patch.circuitLabel.trim() : "";
          if (!trimmed) delete next.circuitLabel;
          else next.circuitLabel = trimmed;
        }
        m.set("attributes", next);
      });
    },
    [doc, maps],
  );

  const buildClipboardPayload = useCallback<DomainMutations["buildClipboardPayload"]>(
    (deviceIds) => {
      const selected = new Set(deviceIds);
      const terminals = listTerminals(maps);
      const deviceById = new Map(listDevices(maps).map((d) => [d.id, d] as const));

      const devices: ClipboardPayload["devices"] = [];
      for (const id of deviceIds) {
        const dev = deviceById.get(id as DeviceId);
        const layout = getDeviceLayout(maps, id as DeviceId, sheetId);
        if (!dev || !layout) continue;
        const symbolId = (dev.attributes as { symbolId?: string })?.symbolId;
        if (!symbolId) continue;
        devices.push({
          oldId: id,
          symbolId,
          position: { x: layout.x, y: layout.y },
          rotation: layout.rotation,
        });
      }

      // Bara kanter där båda ändarna ligger i urvalet följer med.
      const terminalById = new Map(terminals.map((t) => [t.id, t] as const));
      const conductors: ClipboardPayload["conductors"] = [];
      for (const c of listConductors(maps)) {
        const fromT = terminalById.get(c.fromTerminal);
        const toT = terminalById.get(c.toTerminal);
        if (!fromT || !toT) continue;
        if (!selected.has(fromT.deviceId) || !selected.has(toT.deviceId)) continue;
        conductors.push({
          fromDeviceOldId: fromT.deviceId,
          fromSymbolTerminalId: fromT.symbolTerminalId,
          toDeviceOldId: toT.deviceId,
          toSymbolTerminalId: toT.symbolTerminalId,
          attributes: c.attributes,
        });
      }

      return { devices, conductors };
    },
    [maps, sheetId],
  );

  const pasteClipboard = useCallback<DomainMutations["pasteClipboard"]>(
    (payload, offset) => {
      return (
        runLocal(doc, () => {
          const oldToNew = new Map<string, DeviceId>();
          for (const d of payload.devices) {
            const symbol = getSymbol(d.symbolId);
            if (!symbol) continue;
            const existing = listDevices(maps);
            const { device, terminals: newTerms } = createDeviceFromSymbol(symbol, {
              existingDevices: existing,
              symbolId: symbol.id,
            });
            putDevice(maps, device);
            for (const t of newTerms) putTerminal(maps, t);
            putDeviceLayout(maps, {
              deviceId: device.id,
              sheetId,
              x: d.position.x + offset.x,
              y: d.position.y + offset.y,
              rotation: d.rotation,
            });
            oldToNew.set(d.oldId, device.id);
          }

          const terminalsAfter = listTerminals(maps);
          for (const c of payload.conductors) {
            const newFromDev = oldToNew.get(c.fromDeviceOldId);
            const newToDev = oldToNew.get(c.toDeviceOldId);
            if (!newFromDev || !newToDev) continue;
            const fromT = terminalsAfter.find(
              (t) =>
                t.deviceId === newFromDev && t.symbolTerminalId === c.fromSymbolTerminalId,
            );
            const toT = terminalsAfter.find(
              (t) =>
                t.deviceId === newToDev && t.symbolTerminalId === c.toSymbolTerminalId,
            );
            if (!fromT || !toT) continue;
            putConductor(maps, {
              id: conductorId(newId("cond")),
              fromTerminal: fromT.id,
              toTerminal: toT.id,
              attributes: { ...c.attributes },
            });
          }

          return Array.from(oldToNew.values());
        }) ?? []
      );
    },
    [doc, maps, sheetId],
  );

  const groupConductorsAsCable = useCallback<
    DomainMutations["groupConductorsAsCable"]
  >(
    (ids, opts) => {
      if (ids.length === 0) return null;
      return runLocal(doc, () => {
        // Flytta först conductors som redan ligger i andra kablar — annars får
        // vi dubblett-ref:er och den gamla kabeln fortsätter att visa en
        // conductor som flyttats.
        for (const id of ids) {
          removeConductorFromAnyCable(maps, conductorId(id));
        }
        const existing = listCables(maps);
        const designation = opts?.designation?.trim() || nextCableDesignation(existing);
        const cable: Cable = {
          id: mkCableId(newId("cable")),
          designation,
          conductors: ids.map((id) => conductorId(id)),
          type: opts?.type?.trim() ?? "",
        };
        putCable(maps, cable);
        return cable.id;
      });
    },
    [doc, maps],
  );

  const leaveCable = useCallback<DomainMutations["leaveCable"]>(
    (id) => {
      runLocal(doc, () => {
        removeConductorFromAnyCable(maps, conductorId(id));
      });
    },
    [doc, maps],
  );

  const renameCable = useCallback<DomainMutations["renameCable"]>(
    (id, designation) => {
      runLocal(doc, () => {
        const m = maps.cables.get(id);
        if (!m) return;
        const trimmed = designation.trim();
        if (!trimmed) return;
        m.set("designation", trimmed);
      });
    },
    [doc, maps],
  );

  const setCableType = useCallback<DomainMutations["setCableType"]>(
    (id, type) => {
      runLocal(doc, () => {
        const m = maps.cables.get(id);
        if (!m) return;
        m.set("type", type.trim());
      });
    },
    [doc, maps],
  );

  const addSheet = useCallback<DomainMutations["addSheet"]>(
    (opts) => {
      return runLocal(doc, () => {
        const existing = listSheetsInOrder(maps);
        const index = existing.length;
        const title = opts?.title?.trim() || nextSheetTitle(existing);
        const newSheetId = mkSheetId(newId("sheet"));
        const sheet: Sheet = {
          id: newSheetId,
          index,
          title,
          format: "A3",
          orientation: "landscape",
        };
        putSheet(maps, sheet);
        maps.sheetOrder.push([newSheetId]);
        return newSheetId;
      });
    },
    [doc, maps],
  );

  // Klonar hela arket — devices får nya designationer, terminaler och
  // conductors får nya id:n, cables behåller sin gruppering för de duplicerade
  // trådarna men får nya auto-numrerade W-beteckningar.
  const duplicateSheet = useCallback<DomainMutations["duplicateSheet"]>(
    (sourceSheetId) => {
      return (
        runLocal(doc, () => {
          const sourceSheetMap = maps.sheets.get(sourceSheetId);
          if (!sourceSheetMap) return null;
          const sourceTitle = (sourceSheetMap.get("title") as string) ?? "Ark";
          const sourceFormat =
            (sourceSheetMap.get("format") as Sheet["format"]) ?? "A3";
          const sourceOrientation =
            (sourceSheetMap.get("orientation") as Sheet["orientation"]) ??
            "landscape";

          const sourceLayouts = listDeviceLayoutsForSheet(maps, sourceSheetId);
          if (sourceLayouts.length === 0) {
            // Tomt ark — duplicera bara ark-metadatan.
            const existing = listSheetsInOrder(maps);
            const newSheetIdVal = mkSheetId(newId("sheet"));
            putSheet(maps, {
              id: newSheetIdVal,
              index: existing.length,
              title: `${sourceTitle} (kopia)`,
              format: sourceFormat,
              orientation: sourceOrientation,
            });
            maps.sheetOrder.push([newSheetIdVal]);
            return newSheetIdVal;
          }

          const allDevicesBefore = listDevices(maps);
          const allTerminalsBefore = listTerminals(maps);
          const allConductorsBefore = listConductors(maps);
          const allCablesBefore = listCables(maps);
          const sourceDeviceIds = new Set(sourceLayouts.map((l) => l.deviceId));
          const sourceTerminals = allTerminalsBefore.filter((t) =>
            sourceDeviceIds.has(t.deviceId),
          );
          const deviceById = new Map(
            allDevicesBefore.map((d) => [d.id, d] as const),
          );

          // Nytt ark läggs sist så användaren ser det direkt efter befintliga.
          const existingSheets = listSheetsInOrder(maps);
          const newSheetIdVal = mkSheetId(newId("sheet"));
          putSheet(maps, {
            id: newSheetIdVal,
            index: existingSheets.length,
            title: `${sourceTitle} (kopia)`,
            format: sourceFormat,
            orientation: sourceOrientation,
          });
          maps.sheetOrder.push([newSheetIdVal]);

          const oldToNewDevice = new Map<string, DeviceId>();
          const oldToNewTerminal = new Map<string, TerminalId>();

          for (const layout of sourceLayouts) {
            const device = deviceById.get(layout.deviceId);
            if (!device) continue;
            const symbolId =
              typeof device.attributes["symbolId"] === "string"
                ? (device.attributes["symbolId"] as string)
                : undefined;
            if (!symbolId) continue;
            const symbol = getSymbol(symbolId);
            if (!symbol) continue;
            // listDevices mid-transaktion reflekterar redan-skapade kloner så
            // designation-autonumreringen inte kolliderar.
            const existingNow = listDevices(maps);
            const { device: newDevice, terminals: newTerms } =
              createDeviceFromSymbol(symbol, {
                existingDevices: existingNow,
                symbolId,
              });
            putDevice(maps, newDevice);
            for (const t of newTerms) putTerminal(maps, t);
            putDeviceLayout(maps, {
              deviceId: newDevice.id,
              sheetId: newSheetIdVal,
              x: layout.x,
              y: layout.y,
              rotation: layout.rotation,
            });
            oldToNewDevice.set(device.id, newDevice.id);
            const oldDeviceTerminals = sourceTerminals.filter(
              (t) => t.deviceId === device.id,
            );
            for (const oldT of oldDeviceTerminals) {
              const match = newTerms.find(
                (t) => t.symbolTerminalId === oldT.symbolTerminalId,
              );
              if (match) oldToNewTerminal.set(oldT.id, match.id);
            }
          }

          const oldToNewConductor = new Map<string, string>();
          for (const cond of allConductorsBefore) {
            const newFrom = oldToNewTerminal.get(cond.fromTerminal);
            const newTo = oldToNewTerminal.get(cond.toTerminal);
            if (!newFrom || !newTo) continue;
            const newCondId = conductorId(newId("cond"));
            putConductor(maps, {
              id: newCondId,
              fromTerminal: newFrom,
              toTerminal: newTo,
              attributes: { ...cond.attributes },
            });
            oldToNewConductor.set(cond.id, newCondId);
          }

          // Cables: klona endast de där ALLA conductors kunde dupliceras.
          // nextCableDesignation konsulterar maps.cables mid-transaktion så
          // nyss-skapade kablar tas med i nästa auto-nummer.
          for (const cable of allCablesBefore) {
            if (cable.conductors.length === 0) continue;
            const newConductorIds = cable.conductors
              .map((cid) => oldToNewConductor.get(cid as string))
              .filter((x): x is string => Boolean(x));
            if (newConductorIds.length !== cable.conductors.length) continue;
            const designation = nextCableDesignation(listCables(maps));
            const newCable: Cable = {
              id: mkCableId(newId("cable")),
              designation,
              conductors: newConductorIds.map((c) => conductorId(c)),
              type: cable.type,
            };
            putCable(maps, newCable);
          }

          return newSheetIdVal;
        }) ?? null
      );
    },
    [doc, maps],
  );

  const renameSheet = useCallback<DomainMutations["renameSheet"]>(
    (id, title) => {
      runLocal(doc, () => {
        const m = maps.sheets.get(id);
        if (!m) return;
        m.set("title", title);
      });
    },
    [doc, maps],
  );

  const deleteSheet = useCallback<DomainMutations["deleteSheet"]>(
    (id) => {
      return (
        runLocal(doc, () => {
          if (maps.sheetOrder.length <= 1) return false;

          const layoutsToDrop = listDeviceLayoutsForSheet(maps, id);
          const deviceIdsOnSheet = new Set<string>(
            layoutsToDrop.map((l) => l.deviceId),
          );

          deleteLayoutsForSheet(maps, id);
          deleteRoutingsForSheet(maps, id);

          // Cascade: devices som nu saknar layout på något sheet raderas
          // (tillsammans med sina terminaler + conductors). Fas 1 har en
          // device per sheet, så de flesta devices blir föräldralösa direkt.
          for (const deviceIdRaw of deviceIdsOnSheet) {
            const remaining = listSheetsForDevice(maps, deviceIdRaw as DeviceId);
            if (remaining.length > 0) continue;
            cascadeDeleteDevice(maps, deviceIdRaw as DeviceId);
          }

          domainDeleteSheet(maps, id);
          return true;
        }) ?? false
      );
    },
    [doc, maps],
  );

  const moveSheet = useCallback<DomainMutations["moveSheet"]>(
    (id, newIndex) => {
      runLocal(doc, () => {
        moveSheetInOrder(maps, id, newIndex);
      });
    },
    [doc, maps],
  );

  return useMemo(
    () => ({
      addDeviceFromSymbol,
      moveDevice,
      nudgeDevices,
      rotateDevice,
      setDeviceRotation,
      renameDevice,
      connect,
      deleteDevice,
      deleteConductor,
      setConductorAttributes,
      buildClipboardPayload,
      pasteClipboard,
      addSheet,
      duplicateSheet,
      renameSheet,
      deleteSheet,
      moveSheet,
      groupConductorsAsCable,
      leaveCable,
      renameCable,
      setCableType,
    }),
    [
      addDeviceFromSymbol,
      moveDevice,
      nudgeDevices,
      rotateDevice,
      setDeviceRotation,
      renameDevice,
      connect,
      deleteDevice,
      deleteConductor,
      setConductorAttributes,
      buildClipboardPayload,
      pasteClipboard,
      addSheet,
      duplicateSheet,
      renameSheet,
      deleteSheet,
      moveSheet,
      groupConductorsAsCable,
      leaveCable,
      renameCable,
      setCableType,
    ],
  );
}

/**
 * "Ark N" där N = högsta existerande "Ark n" + 1. Respekterar användar-
 * omdöpta sheets (de påverkar inte nästa nummer) genom att bara titta på
 * titlar som exakt matchar mönstret.
 */
function nextSheetTitle(existing: ReadonlyArray<Sheet>): string {
  const re = /^Ark\s+(\d+)$/i;
  let max = 0;
  for (const s of existing) {
    const m = re.exec(s.title);
    if (!m || !m[1]) continue;
    const n = Number.parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `Ark ${max + 1}`;
}

/**
 * "W N" enligt SS-EN 81346 (W är bokstavskoden för ledningar/kabel). Returnerar
 * första lediga heltalet: W1, W2, W3… Icke-matchande designations (t.ex. "kraft")
 * räknas inte in, så användaren kan byta namn utan att blockera auto-nummer.
 */
function nextCableDesignation(existing: ReadonlyArray<Cable>): string {
  const used = new Set<number>();
  const re = /^W(\d+)$/i;
  for (const c of existing) {
    const m = re.exec(c.designation);
    if (!m || !m[1]) continue;
    const n = Number.parseInt(m[1], 10);
    if (Number.isFinite(n)) used.add(n);
  }
  let n = 1;
  while (used.has(n)) n += 1;
  return `W${n}`;
}

/**
 * Raderar device + dess terminaler + conductors som refererar dessa terminaler
 * + alla layouts på alla sheets. Måste köras inne i en runLocal-transaktion.
 * Samma cascade som deleteDevice gör; extraherad för att deleteSheet ska kunna
 * återanvända den utan att starta en ny transaktion.
 */
function cascadeDeleteDevice(maps: DomainMaps, id: DeviceId): void {
  maps.devices.delete(id);

  const layoutKeysToDrop: string[] = [];
  maps.deviceLayout.forEach((_, key) => {
    if (key.startsWith(`${id}:`)) layoutKeysToDrop.push(key);
  });
  for (const k of layoutKeysToDrop) maps.deviceLayout.delete(k);

  const termIdsToDelete: TerminalId[] = [];
  for (const t of listTerminals(maps)) {
    if (t.deviceId === id) termIdsToDelete.push(t.id);
  }
  const termSet = new Set<string>(termIdsToDelete);
  for (const tid of termIdsToDelete) maps.terminals.delete(tid);

  const condsToDelete: string[] = [];
  maps.conductors.forEach((m, cid) => {
    const from = m.get("fromTerminal");
    const to = m.get("toTerminal");
    if (
      (typeof from === "string" && termSet.has(from)) ||
      (typeof to === "string" && termSet.has(to))
    ) {
      condsToDelete.push(cid);
    }
  });
  for (const cid of condsToDelete) {
    // Rensa cable-ref innan conductorn tas bort så ingen tom kabel ligger kvar.
    removeConductorFromAnyCable(maps, conductorId(cid));
    maps.conductors.delete(cid);
  }
}

function findTerminal(
  terminals: Terminal[],
  deviceId: string,
  symbolTerminalId: string,
): Terminal | undefined {
  return terminals.find(
    (t) => t.deviceId === deviceId && t.symbolTerminalId === symbolTerminalId,
  );
}
